import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDebounce } from 'use-debounce';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';

const FetchAutoComplete = function FetchAutoComplete({
  id = 'id-autocomplete',
  label = '',
  placeholder = '',
  width = '100%',
  selectedValue = null,
  multiple = false,
  allUpperCase = false,
  getOptionLabel = (option) => option,
  onChangeSelectedValue,
  fetchCallback,
  staticOptions = null,
  noOptionsText,
  noCustomValue = false,
  ...fieldProps //all others autocomplete props
}) {
  const intl = useIntl();
  const [inputValue, setInputValue] = useState(''); //typing value
  const [options, setOptions] = useState([]);
  const [inPending, setInPending] = useState(false);
  const [debouncedValue] = useDebounce(inputValue, 600); //wait after finish typing
  const autocompleteValue = multiple
    ? Array.isArray(selectedValue)
      ? selectedValue
      : selectedValue
        ? [selectedValue]
        : []
    : selectedValue;

  const handleChangeInputValue = (event, newValue) => {
    if (allUpperCase) setInputValue(newValue.toUpperCase());
    else setInputValue(newValue);
  };

  useEffect(() => {
    if (Array.isArray(staticOptions)) {
      setOptions(staticOptions);
    }
  }, [staticOptions]);

  useEffect(() => {
    if (Array.isArray(staticOptions)) {
      return;
    }

    if (!fetchCallback) {
      return;
    }

    if (inputValue) {
      setInPending(true);
      fetchCallback(
        inputValue,
        (res) => {
          setOptions(res.length > 0 ? res : noCustomValue ? [] : [inputValue]);
          return Promise.resolve('loaded');
        },
        () => {
          setOptions([]);
          return Promise.resolve('loaded');
        }
      ).finally(() => {
        setInPending(false);
      });
    }
  }, [debouncedValue]);

  return (
    <Autocomplete
      id={id}
      multiple={multiple}
      loading={inPending}
      value={autocompleteValue}
      onChange={onChangeSelectedValue}
      inputValue={inputValue}
      onInputChange={handleChangeInputValue}
      disablePortal
      disableCloseOnSelect={multiple}
      filterSelectedOptions={multiple}
      options={options}
      fullWidth={width === '100%'}
      sx={{ width: width !== '100%' ? width : null }}
      getOptionLabel={getOptionLabel}
      noOptionsText={noOptionsText ?? intl.messages.NO_OPTIONS}
      renderInput={(params) => (
        <TextField
          {...params}
          {...fieldProps}
          label={label}
          variant="outlined"
          FormHelperTextProps={{
            sx: {
              whiteSpace: 'noWrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {inPending ? <CircularProgress size={20} /> : null}
                {params.InputProps?.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

FetchAutoComplete.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  selectedValue: PropTypes.any,
  multiple: PropTypes.bool,
  onChangeSelectedValue: PropTypes.func.isRequired,
  fetchCallback: PropTypes.func,
  allUpperCase: PropTypes.bool,
  width: PropTypes.string,
  getOptionLabel: PropTypes.func,
  staticOptions: PropTypes.array,
};

export default FetchAutoComplete;
